/**
 * useProtectedAction Hook
 *
 * Security bottleneck for all authenticated actions.
 * Ensures consistent auth checks across the entire app.
 *
 * This hook is the SINGLE SOURCE OF TRUTH for:
 * - Authentication checks
 * - Intent storage/retrieval
 * - Sign-in redirects/modals
 *
 * All features requiring auth MUST go through this hook.
 */
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useNavigationStack } from '../contexts/NavigationContext';

export function useProtectedAction() {
  const { currentUser, loading } = useAuth();
  const navigate = useNavigate();
  const navigationContext = useNavigationStack?.() || null;

  /**
   * Require authentication before executing an action.
   * This is the BOTTLENECK that prevents malicious use.
   *
   * @param {string} action - The intended action ("search" | "profile" | "upload")
   * @param {function} onSuccess - Callback to execute if authenticated
   * @param {function} onCancel - Optional callback if user cancels
   * @param {boolean} useModal - Use modal auth gate instead of redirect (default: false for backward compatibility)
   * @returns {boolean} - True if action was executed, false if auth required
   */
  const requireAuth = (action, onSuccess, onCancel = null, useModal = false) => {
    // Loading state - prevent action until we know auth status
    if (loading) {
      console.log('[ProtectedAction] Still loading auth state, blocking action');
      return false;
    }

    // User is authenticated - allow action
    if (currentUser) {
      console.log('[ProtectedAction] User authenticated, executing action:', action);
      onSuccess();
      return true;
    }

    // User NOT authenticated - save intent
    console.log('[ProtectedAction] User not authenticated, showing auth gate');
    localStorage.setItem('intendedAction', action);
    localStorage.setItem('intendedActionTimestamp', Date.now().toString());

    // Use modal auth gate if NavigationContext available and requested
    if (useModal && navigationContext?.navigateWithTransition) {
      navigationContext.navigateWithTransition('/auth', {
        direction: 'modal',
        state: {
          intendedAction: action,
          message: getIntentMessage(action),
          returnPath: window.location.pathname
        }
      });
    } else {
      // Fallback to traditional navigation
      navigate('/auth', {
        state: {
          intendedAction: action,
          message: getIntentMessage(action)
        }
      });
    }

    return false;
  };

  /**
   * Check if user is authenticated (non-blocking)
   * Use this for UI conditional rendering
   */
  const isAuthenticated = () => {
    return !loading && currentUser !== null;
  };

  /**
   * Clear intended action (after successful sign-in or cancel)
   */
  const clearIntendedAction = () => {
    localStorage.removeItem('intendedAction');
    localStorage.removeItem('intendedActionTimestamp');
  };

  /**
   * Get current intended action (if any)
   */
  const getIntendedAction = () => {
    const action = localStorage.getItem('intendedAction');
    const timestamp = localStorage.getItem('intendedActionTimestamp');

    // Expire intent after 10 minutes
    if (action && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age > 10 * 60 * 1000) {
        clearIntendedAction();
        return null;
      }
    }

    return action;
  };

  /**
   * Get user-friendly message for intent
   */
  const getIntentMessage = (action) => {
    const messages = {
      'search': 'Sign in for personalized search results',
      'upload': 'Sign in to upload and search with your image',
      'profile': 'Sign in to view your profile',
      'save': 'Sign in to save your favorite looks',
      'preferences': 'Sign in to customize your preferences',
      'chat': 'Sign in to chat with Leo, your AI stylist'
    };

    return messages[action] || 'Sign in to continue';
  };

  /**
   * Resume intended action after successful sign-in
   */
  const resumeIntendedAction = () => {
    const action = getIntendedAction();
    console.log('[ProtectedAction] Attempting to resume action:', action || 'none');
    clearIntendedAction();

    if (!action) {
      console.log('[ProtectedAction] No intended action, navigating to home');
      navigate('/');
      return;
    }

    console.log('[ProtectedAction] Resuming intended action:', action);

    // Route based on action
    switch (action) {
      case 'search':
      case 'upload':
        console.log('[ProtectedAction] Navigating to /search');
        navigate('/search');
        break;
      case 'chat':
        console.log('[ProtectedAction] Navigating to /chat/leo');
        navigate('/chat/leo');
        break;
      case 'profile':
        console.log('[ProtectedAction] Navigating to /profile');
        navigate('/profile');
        break;
      case 'save':
        console.log('[ProtectedAction] Navigating to /saved');
        navigate('/saved');
        break;
      default:
        console.log('[ProtectedAction] Unknown action, navigating to home');
        navigate('/');
    }
  };

  return {
    requireAuth,
    isAuthenticated,
    clearIntendedAction,
    getIntendedAction,
    getIntentMessage,
    resumeIntendedAction,
    loading,
    currentUser
  };
}


